import { useEffect, useState } from 'react'
import BaseIpc from '@renderer/ipc/BaseIpc.renderer'
import type { PreferredHourCycle } from '@shared/ipc/routers/BaseIpc.router'

let cachedHourCycle: PreferredHourCycle | null | undefined
let pendingHourCycle: Promise<PreferredHourCycle | null> | null = null

function loadPreferredHourCycle(): Promise<PreferredHourCycle | null> {
  if (cachedHourCycle !== undefined) return Promise.resolve(cachedHourCycle)
  if (!pendingHourCycle) {
    pendingHourCycle = BaseIpc.getPreferredHourCycle()
      .then((hourCycle) => {
        cachedHourCycle = hourCycle
        return hourCycle
      })
      .catch(() => {
        cachedHourCycle = null
        return null
      })
  }
  return pendingHourCycle
}

export function usePreferredHourCycle(): PreferredHourCycle | null {
  const [hourCycle, setHourCycle] = useState<PreferredHourCycle | null>(cachedHourCycle ?? null)

  useEffect(() => {
    let cancelled = false
    void loadPreferredHourCycle().then((resolvedHourCycle) => {
      if (!cancelled) setHourCycle(resolvedHourCycle)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return hourCycle
}

function withHourCycle(
  options: Intl.DateTimeFormatOptions,
  hourCycle: PreferredHourCycle | null,
): Intl.DateTimeFormatOptions {
  return hourCycle ? { ...options, hourCycle } : options
}

export function formatUserTime(
  date: Date,
  hourCycle: PreferredHourCycle | null,
  { omitZeroMinutes = false }: { omitZeroMinutes?: boolean } = {},
): string {
  const uses24HourTime = hourCycle === 'h23' || hourCycle === 'h24'
  const includeMinute = !omitZeroMinutes || uses24HourTime || date.getMinutes() !== 0

  return new Intl.DateTimeFormat(
    undefined,
    withHourCycle({ hour: 'numeric', ...(includeMinute && { minute: '2-digit' }) }, hourCycle),
  ).format(date)
}

export function formatUserDateTime(date: Date, hourCycle: PreferredHourCycle | null): string {
  return new Intl.DateTimeFormat(
    undefined,
    withHourCycle({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, hourCycle),
  ).format(date)
}
